import React, { useEffect, useMemo, useRef, useState } from 'react';
import AskIcon from 'shared/components/ui/AskIcon';
import { createPortal } from 'react-dom';
import {
  Search, X, Plus, Sparkles, LayoutGrid, MessageSquare, Gauge, ShieldCheck,
  Wrench, Coins, Users, Target, Code2, Brain, UserCheck, ListChecks,
} from 'lucide-react';
import { listTemplates } from 'features/evals3/api/evals3Api';
import { TargetPill } from 'features/evals3/components/EvalsTable';

/**
 * "New eval": start from a template, from scratch, or by describing it to Ask.
 *
 * The templates are the server's catalog (GET /eval3/templates) — the common
 * evals, each a complete eval to adapt. Search reads a template's name,
 * description, when-to-use line and the kinds of step it uses; the category
 * chips narrow it. Picking one opens the create page filled in from it;
 * nothing is saved until the person creates it.
 */

const USES = { python: 'Python', judge: 'LLM judge', decision: 'Decision model', semantic: 'Semantic check', human: 'Human review' };

/** One mark per kind of step, so a row says how it decides before it is read. */
const USE_ICON = { python: Code2, judge: Sparkles, decision: ListChecks, semantic: Brain, human: UserCheck };

/**
 * A face for each category. Falls back to a target when the catalog grows a
 * category this does not know, so a new one is never iconless.
 */
const CAT_ICON = {
  all: LayoutGrid,
  quality: Gauge,
  grounding: Target,
  safety: ShieldCheck,
  tools: Wrench,
  performance: Coins,
  conversation: MessageSquare,
  review: Users,
};
const iconFor = (id) => CAT_ICON[id] || Target;

/** The three to reach for first: one of each way of deciding. */
const START_HERE = ['rag-faithfulness', 'answer-relevance', 'tool-call-succeeded'];

/* The dialog rises in, and the three starting points arrive just behind it.
   Both are off under prefers-reduced-motion: the point is a sense of the
   panel arriving, never a wait. */
const MOTION = `
.eval-template-dialog { animation: evalDialogIn 160ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.eval-template-card { animation: evalCardIn 220ms cubic-bezier(0.16, 1, 0.3, 1) both; }
@keyframes evalDialogIn { from { opacity: 0; transform: translateY(-8px) scale(0.985); } to { opacity: 1; transform: none; } }
@keyframes evalCardIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .eval-template-dialog, .eval-template-card { animation: none; }
}`;

export function matchesTemplate(t, q) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const hay = [t.name, t.description, t.when, t.category, t.target, ...(t.uses || []).map((u) => USES[u] || u)].join(' ').toLowerCase();
  return s.split(/\s+/).every((w) => hay.includes(w));
}

export default function NewEvalDialog({ teamId, open, onClose, onPick, onScratch, onAsk }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || data) return undefined;
    let off = false;
    Promise.resolve(listTemplates(teamId))
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off) setError(e?.response?.data?.error || e.message); });
    return () => { off = true; };
  }, [open, teamId, data]);

  useEffect(() => {
    if (!open) return undefined;
    setQ(''); setCat('all');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  // Memoised, not defaulted inline: `|| []` is a fresh array every render, and
  // every list below it would recompute on every keystroke.
  const categories = useMemo(() => data?.categories || [], [data]);
  const all = useMemo(() => data?.templates || [], [data]);
  const shown = useMemo(() => all.filter((t) => (cat === 'all' || t.category === cat) && matchesTemplate(t, q)), [all, cat, q]);
  const grouped = useMemo(() => categories
    .map((c) => ({ ...c, items: shown.filter((t) => t.category === c.id) }))
    .filter((g) => g.items.length), [categories, shown]);
  const counts = useMemo(() => {
    const m = { all: all.length };
    for (const t of all) m[t.category] = (m[t.category] || 0) + 1;
    return m;
  }, [all]);
  // Three to reach for first, one per way of deciding. Only when nothing is
  // being searched or narrowed: once someone is looking for something, the
  // thing they are looking for is the point, not our suggestions.
  const starters = useMemo(() => (q.trim() || cat !== 'all'
    ? []
    : START_HERE.map((id) => all.find((t) => t.id === id)).filter(Boolean)), [all, q, cat]);

  if (!open) return null;

  const el = (
    <div className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[7vh]"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog" aria-modal="true" aria-labelledby="new-eval-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{MOTION}</style>
      <div className="eval-template-dialog flex max-h-[84vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl"
        style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-3">
          <h2 id="new-eval-title" className="text-[14px] font-semibold text-[color:var(--text-primary)]">New eval</h2>
          <div className="flex-1" />
          <button type="button" onClick={onAsk} title="Describe the eval to Lucid: it reads your traces, builds it, and shows what it would decide"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
            <AskIcon size={14} /> Create with Lucid
          </button>
          <button type="button" onClick={onScratch}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover">
            <Plus size={13} aria-hidden /> Start from scratch
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>

        {/* Search stays full width above the split: it searches everything,
            not the category in the rail. */}
        <div className="border-b border-[color:var(--border-subtle)] px-4 py-2.5">
          <div className="flex h-9 items-center gap-2 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 focus-within:border-[color:var(--border-strong)]">
            <Search size={14} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates: hallucination, tool errors, frustration, JSON…"
              aria-label="Search templates" className="min-w-0 flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]" />
            {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search" className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X size={13} /></button>}
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Browse rail: every category with how many it holds, so the shape of
              the catalog is visible before anything is clicked. */}
          <nav className="hidden w-[186px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-[color:var(--border-subtle)] px-2 py-2.5 sm:flex" aria-label="Categories">
            <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">Browse</div>
            {[{ id: 'all', label: 'All' }, ...categories].map((c) => {
              const Icon = iconFor(c.id);
              const on = cat === c.id;
              return (
                <button key={c.id} type="button" role="radio" aria-checked={on} onClick={() => setCat(c.id)}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] transition-colors ${on
                    ? 'bg-surface-hover font-medium text-[color:var(--text-primary)]'
                    : 'text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'}`}>
                  <Icon size={14} className={on ? 'text-brand' : 'text-[color:var(--text-muted)]'} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{c.label}</span>
                  <span className="shrink-0 text-[10.5px] tabular-nums text-[color:var(--text-muted)]">{counts[c.id] || 0}</span>
                </button>
              );
            })}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid="template-list">
            {error && <div className="text-[12px] text-danger" role="alert">Templates could not load: {error}</div>}
            {!data && !error && <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">Loading templates…</div>}
            {data && shown.length === 0 && (
              <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                No template matches “{q}”. <button type="button" onClick={onScratch} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">Start from scratch</button> or <button type="button" onClick={onAsk} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">describe it to Lucid</button>.
              </div>
            )}

            {starters.length > 0 && (
              <section className="mb-5" aria-label="Start here">
                <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Start here</h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {starters.map((t, i) => {
                    const Icon = iconFor(t.category);
                    return (
                      <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`starter-${t.id}`}
                        style={{ animationDelay: `${i * 45}ms` }}
                        className="eval-template-card group flex flex-col items-start gap-1.5 rounded-xl border border-[color:var(--border-default)] bg-surface p-3 text-left transition-all hover:-translate-y-0.5 hover:border-[color:var(--border-strong)] hover:shadow-token-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]">
                        <span className="flex w-full items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-soft text-brand transition-transform group-hover:scale-105">
                            <Icon size={14} aria-hidden />
                          </span>
                          <TargetPill target={t.target} />
                        </span>
                        <span className="text-[13px] font-medium leading-snug text-[color:var(--text-primary)]">{t.name}</span>
                        <span className="line-clamp-2 text-[11.5px] leading-snug text-[color:var(--text-secondary)]">{t.description}</span>
                        <span className="text-[10.5px] text-[color:var(--text-muted)]">{(t.uses || []).map((u) => USES[u] || u).join(' · ')}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {grouped.map((g) => (
              <section key={g.id} className="mb-4" aria-label={g.label}>
                <h3 className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                  {React.createElement(iconFor(g.id), { size: 12, 'aria-hidden': true })}
                  {g.label}
                </h3>
                <div className="flex flex-col">
                  {g.items.map((t) => (
                    <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`template-${t.id}`}
                      className="group -mx-1.5 flex items-center gap-3 rounded-lg px-1.5 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]">
                      <span className="flex w-[210px] shrink-0 items-center gap-2">
                        <span className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">{t.name}</span>
                        <TargetPill target={t.target} />
                      </span>
                      <span className="line-clamp-1 min-w-0 flex-1 text-[12px] text-[color:var(--text-secondary)]">{t.description}</span>
                      {/* How it decides, in words. A mark alone looks tidy and
                          tells a first-time reader nothing. */}
                      <span className="flex shrink-0 items-center gap-1.5">
                        {(t.uses || []).map((u) => {
                          const I = USE_ICON[u];
                          return (
                            <span key={u} className="inline-flex items-center gap-1 rounded-md bg-[color:var(--surface-sunken)] px-1.5 py-0.5 text-[10.5px] text-[color:var(--text-muted)]">
                              {I ? <I size={10} aria-hidden /> : null}{USES[u] || u}
                            </span>
                          );
                        })}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(el, document.body);
}
